import { Stack, useRouter, Link } from 'expo-router';
import { Keyboard, Platform, View, ScrollView } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import CommunityHeroBanner from '@/components/ui/CommunityHeroBanner';
import { Text } from '@/components/gluestack/text';
import { z } from 'zod';
import {
  CheckIcon,
  EyeIcon,
  EyeOffIcon,
  AlertCircleIcon,
  CloseIcon,
} from '@/components/gluestack/icon';
import React, { useState, useEffect, useRef } from 'react';
import { Button, ButtonText, ButtonIcon } from '@/components/gluestack/button';
import {
  Checkbox,
  CheckboxIndicator,
  CheckboxIcon,
  CheckboxLabel,
} from '~/components/gluestack/checkbox';
import {
  FormControl,
  FormControlLabel,
  FormControlLabelText,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '~/components/gluestack/form-control';
import { Heading } from '@/components/gluestack/heading';
import { HStack } from '@/components/gluestack/hstack';
import { Input, InputField, InputSlot, InputIcon } from '@/components/gluestack/input';
import { VStack } from '@/components/gluestack/vstack';
import { useForm, Controller } from 'react-hook-form';
import { Toast, ToastTitle, useToast } from '@/components/gluestack/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '~/components/providers/AuthProvider';
import { CommunityThemeScope } from '~/components/ui/CommunityThemeScope';
import { communities, type CommunityCode } from '@/constants/communities';
import { useTranslation } from 'react-i18next';
import { ROUTES, FEED_ROUTES } from '@/constants/routes';
import { useDefaultFeed } from '@/hooks/useDefaultFeed';
import { WebDocumentTitle } from '@/components/layout/WebDocumentTitle';
import {
  type AppLinkRedirectMode,
  EXTERNAL_APP_LINK_REDIRECT_MODE,
  useAppLink,
} from '@/components/providers/AppLinkProvider';

const createLoginSchema = (t: (key: string, options?: any) => string) =>
  z.object({
    username: z.string().min(1, t('common.validation.requiredField', { field: t('auth.signIn.usernameLabel') })),
    password: z.string().min(1, t('common.validation.requiredField', { field: t('auth.signIn.passwordLabel') })),
    rememberme: z.boolean().optional(),
  });

type LoginSchemaType = z.infer<ReturnType<typeof createLoginSchema>>;

export interface ISignInScreenProps {
  community: CommunityCode;
  redirect?: string;
  redirectMode?: AppLinkRedirectMode;
  title?: string;
}

const getSafeRedirectPath = (redirect: string | undefined): string | undefined => {
  if (!redirect) return undefined;

  try {
    if (!redirect.startsWith('/') || redirect.startsWith('//') || redirect.includes('\\')) {
      return undefined;
    }

    const baseOrigin =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.origin
        : 'https://grokio.invalid';
    const url = new URL(redirect, baseOrigin);
    // Dot segments can normalize to a protocol-relative path (`/.//evil.com` → `//evil.com`),
    // which the router would open as an external link.
    if (url.origin !== baseOrigin || url.pathname.startsWith('//')) return undefined;

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
};

export default function SignInScreen({ community, redirect, redirectMode, title }: ISignInScreenProps) {
  const { t } = useTranslation();
  const loginSchema = React.useMemo(() => createLoginSchema(t), [t]);
  const { signIn, getCredentials, forget } = useAuth();
  const { openExternalAppLinkPath } = useAppLink();
  const { defaultFeed } = useDefaultFeed();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<LoginSchemaType>({
    resolver: zodResolver(loginSchema),
  });
  const toast = useToast();
  const [validated, setValidated] = useState({
    usernameValid: true,
    passwordValid: true,
  });
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const router = useRouter();
  const passwordInputRef = useRef<any>(null);

  useEffect(() => {
    if (community) {
      const savedCredentials = getCredentials(community as CommunityCode);
      if (savedCredentials) {
        setValue('username', savedCredentials.UserName || '');
        setValue('password', '');
      } else {
        setValue('username', '');
        setValue('password', '');
      }
    }
  }, [community, getCredentials, setValue]);

  const hasSavedCredentials = !!getCredentials(community as CommunityCode);

  const handleForgetCredentials = async () => {
    try {
      await forget(community as CommunityCode);
      setValue('username', '');
      setValue('password', '');
    } catch (error) {
      toast.show({
        placement: 'bottom right',
        render: ({ id }) => (
          <Toast nativeID={id} variant="solid" action="error">
            <ToastTitle>
              {error instanceof Error ? error.message : t('auth.signIn.removeCredentialsError')}
            </ToastTitle>
          </Toast>
        ),
      });
    }
  };

  const onSubmit = async (data: LoginSchemaType) => {
    setLoading(true);
    setGeneralError(null);
    try {
      await signIn({
        community: community as CommunityCode,
        username: data.username,
        password: data.password,
        rememberMe: data.rememberme,
      });

      setValidated({ usernameValid: true, passwordValid: true });
      reset();
      const redirectPath = getSafeRedirectPath(redirect);
      if (redirectPath) {
        if (redirectMode === EXTERNAL_APP_LINK_REDIRECT_MODE) {
          openExternalAppLinkPath(redirectPath, community);
          return;
        }

        router.replace(redirectPath as any);
      } else {
        router.replace(FEED_ROUTES[defaultFeed] ?? ROUTES.FEEDS_GLOBAL);
      }
    } catch (error: any) {
      if (error instanceof Error && error.message.includes('password')) {
        setValidated({ usernameValid: true, passwordValid: false });
      } else if (error instanceof Error && error.message.includes('username')) {
        setValidated({ usernameValid: false, passwordValid: true });
      } else {
        setValidated({ usernameValid: true, passwordValid: true });
        const errorMessage = error?.status === 403
          ? t('common.error.ipBlocked')
          : error instanceof Error 
            ? error.message 
            : t('auth.signIn.unexpectedError');
        setGeneralError(errorMessage);
      }

      toast.show({
        placement: 'bottom right',
        render: ({ id }) => (
          <Toast nativeID={id} variant="solid" action="error">
            <ToastTitle>{t('auth.signIn.loginFailedToast')}</ToastTitle>
          </Toast>
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  const [showPassword, setShowPassword] = useState(false);
  const handleState = () => {
    setShowPassword((s) => !s);
  };
  const handleKeyPress = () => {
    Keyboard.dismiss();
    handleSubmit(onSubmit)();
  };

  const handleUsernameSubmit = () => {
    // Focus the password field when Next is pressed on username
    passwordInputRef.current?.focus();
  };

  // Banner image rendered via CommunityHeroBanner for DRYness

  return (
    <>
      <WebDocumentTitle title={title ?? t('auth.signIn.title')} appName={communities[community].name} />
      <Stack.Screen options={{ title: title ?? t('auth.signIn.title') }} />
      <CommunityThemeScope community={community as CommunityCode}>
        <KeyboardAvoidingView
          behavior="padding"
          style={{ flex: 1 }}>
          <View className="flex-1 bg-background-0">
          <ScrollView
            className="flex-1"
            contentContainerStyle={{ flexGrow: 1 }}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}>
            <VStack className="mx-auto w-full pb-24 sm:mt-8 sm:max-w-[440px]" space="md">
              <View className="aspect-[1248/702] w-full overflow-hidden sm:rounded-lg">
                <CommunityHeroBanner
                  community={community as any}
                  contentFit="contain"
                  className="h-full w-full"
                />
              </View>

              <VStack className="justify-between px-4 sm:px-0" space="md">
                <VStack>
                  <Text>{t('auth.signIn.welcomeBack')}</Text>
                  <Heading size="xl">
                    {t('auth.signIn.heading', { communityName: communities[community].name })}
                  </Heading>
                </VStack>
              </VStack>

              <VStack className="mt-4 w-full px-4 sm:px-0">
                <VStack space="xl" className="w-full">
                  <FormControl
                    isInvalid={!!errors?.username || !validated.usernameValid}
                    className="w-full">
                    <FormControlLabel>
                      <FormControlLabelText>{t('auth.signIn.usernameLabel')}</FormControlLabelText>
                    </FormControlLabel>
                    <Controller
                      defaultValue=""
                      name="username"
                      control={control}
                      rules={{
                        validate: async (value) => {
                          try {
                            await loginSchema.parseAsync({ username: value });
                            return true;
                          } catch (error: any) {
                            return error.message;
                          }
                        },
                      }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Input>
                          <InputField
                            placeholder={t('auth.signIn.usernamePlaceholder')}
                            accessibilityLabel={t('auth.signIn.usernameLabel')}
                            placeholderTextColor={undefined}
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            onSubmitEditing={handleUsernameSubmit}
                            returnKeyType="next"
                            autoCapitalize="none"
                            autoCorrect={false}
                          />
                          {hasSavedCredentials && (
                            <InputSlot onPress={handleForgetCredentials} className="pr-3">
                              <InputIcon as={CloseIcon} />
                            </InputSlot>
                          )}
                        </Input>
                      )}
                    />
                    <FormControlError>
                      <FormControlErrorIcon as={AlertCircleIcon} />
                      <FormControlErrorText>
                        {errors?.username?.message ||
                          (!validated.usernameValid && t('auth.signIn.usernameNotFound'))}
                      </FormControlErrorText>
                    </FormControlError>
                  </FormControl>

                  <FormControl
                    isInvalid={!!errors?.password || !validated.passwordValid}
                    className="w-full">
                    <FormControlLabel>
                      <FormControlLabelText>{t('auth.signIn.passwordLabel')}</FormControlLabelText>
                    </FormControlLabel>
                    <Controller
                      defaultValue=""
                      name="password"
                      control={control}
                      rules={{
                        validate: async (value) => {
                          try {
                            await loginSchema.parseAsync({ password: value });
                            return true;
                          } catch (error: any) {
                            return error.message;
                          }
                        },
                      }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Input>
                          <InputField
                            ref={passwordInputRef}
                            type={showPassword ? 'text' : 'password'}
                            placeholder={t('auth.signIn.passwordPlaceholder')}
                            accessibilityLabel={t('auth.signIn.passwordLabel')}
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            onSubmitEditing={handleKeyPress}
                            returnKeyType="done"
                            autoCapitalize="none"
                            autoCorrect={false}
                          />
                          <InputSlot onPress={handleState} className="pr-3">
                            <InputIcon as={showPassword ? EyeIcon : EyeOffIcon} />
                          </InputSlot>
                        </Input>
                      )}
                    />
                    <FormControlError>
                      <FormControlErrorIcon as={AlertCircleIcon} />
                      <FormControlErrorText>
                        {errors?.password?.message ||
                          (!validated.passwordValid && t('auth.signIn.passwordIncorrect'))}
                      </FormControlErrorText>
                    </FormControlError>
                  </FormControl>

                  <HStack className="w-full items-baseline justify-between">
                    {Platform.OS === 'web' && (
                      <Controller
                        name="rememberme"
                        defaultValue={true}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <Checkbox
                            size="sm"
                            value={t('auth.signIn.rememberMe')}
                            isChecked={value}
                            onChange={onChange}
                            aria-label={t('auth.signIn.rememberMe')}>
                            <CheckboxIndicator className="rounded-sm">
                              <CheckboxIcon as={CheckIcon} />
                            </CheckboxIndicator>
                            <CheckboxLabel>{t('auth.signIn.rememberMe')}</CheckboxLabel>
                          </Checkbox>
                        )}
                      />
                    )}
                    {Platform.OS !== 'web' && <View className="flex-1" />}
                    <Link
                      href={{ pathname: `/communities/[community]/forgot-password`, params: { community } }}
                      asChild>
                      <Button variant="link" action="primary" size="sm" className="h-auto p-0">
                        <ButtonText className="text-sm font-medium">
                          {t('auth.signIn.forgotPassword')}
                        </ButtonText>
                      </Button>
                    </Link>
                  </HStack>
                </VStack>

                <VStack className="my-7 w-full " space="lg">
                  <Button className="w-full" onPress={handleSubmit(onSubmit)} isDisabled={loading}>
                    <ButtonText className="font-medium">
                      {loading ? t('auth.signIn.submitting') : t('auth.signIn.submit')}
                    </ButtonText>
                  </Button>
                  {generalError && (
                    <FormControl isInvalid={!!generalError} className="w-full">
                      <FormControlError>
                        <FormControlErrorIcon as={AlertCircleIcon} />
                        <FormControlErrorText>{generalError}</FormControlErrorText>
                      </FormControlError>
                    </FormControl>
                  )}
                </VStack>
                <HStack className="items-baseline self-center" space="sm">
                  <Text size="md">{t('auth.signIn.noAccount')}</Text>
                  <Link
                    href={{ pathname: `/communities/[community]/sign-up`, params: { community: community } }}
                    asChild>
                    <Button variant="link" action="primary" size="md" className="h-auto p-0">
                      <ButtonText className="font-medium">{t('auth.signIn.signUp')}</ButtonText>
                    </Button>
                  </Link>
                </HStack>
              </VStack>
            </VStack>
          </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </CommunityThemeScope>
    </>
  );
}
